import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, authFetchJson } from "@/lib/api";
import { AdminLexiconImport } from "./AdminLexiconImport";
import { AdminLexiconAudit } from "./AdminLexiconAudit";
import { effectivePropagation, propagationLabel } from "./lexiconPropagation";
import {
  OCCURRENCE_WEIGHTS,
  buildCreatePayload,
  buildPatchPayload,
  emptyLexiconFormState,
  isLexiconRowEditable,
  lexiconFormStateFromRow,
  type LexiconAllowedValues,
  type LexiconFormState,
} from "./lexiconRowForm";

export type LexiconRow = {
  id: string;
  term: string;
  subgroup: string;
  discipline: string;
  occurrence_weight: number;
  entry_kind: string;
  canonical_term: string | null;
  category: string | null;
  usage_contexts: string[];
  phonetic_alternative_1: string | null;
  phonetic_alternative_2: string | null;
  source: string;
  source_key: string;
  active: boolean;
  managed: boolean;
};

export type SubTab = "termen" | "import" | "audit";

type RowsResponse = { rows: LexiconRow[]; total: number };
type CountsResponse = {
  total: number;
  classified: number;
  uncategorized: number;
  by_category: Record<string, number>;
  categories: string[];
  entry_kinds: string[];
};

// While /category-counts hasn't answered yet (or answered before this field
// existed on an older cached response), the form's selects have nothing to
// offer — never invent a fallback list here, since that would be exactly
// the second hardcoded copy this was fixed to remove.
const NO_ALLOWED_VALUES: LexiconAllowedValues = { categories: [], entryKinds: [] };
type RowWriteResponse = { row: LexiconRow; epoch: number };

export function AdminLexiconTab() {
  const [subTab, setSubTab] = useState<SubTab>("termen");
  const [rows, setRows] = useState<LexiconRow[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<CountsResponse | null>(null);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // "+ Term" create form.
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState<LexiconFormState>(emptyLexiconFormState());
  const [createErrors, setCreateErrors] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);

  // Inline edit of a single row. Only one row edits at a time.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<LexiconFormState>(emptyLexiconFormState());
  const [editErrors, setEditErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  // A short honest line after a create/edit lands, reusing the propagation
  // vocabulary the import tab already established — not a poller, just the
  // same "this needs a moment to reach every pod" wording.
  const [changeNotice, setChangeNotice] = useState("");

  // The category/entry_kind selects' real options, served by
  // /category-counts (fetched once per tab load, not per keystroke — see
  // loadCounts below) rather than hardcoded in the frontend. Empty until
  // that first response lands. Memoized so it is referentially stable
  // between renders where `counts` hasn't changed — submitCreate/submitEdit
  // depend on it, and without this they would be rebuilt (and any effect
  // depending on them re-run) on every render.
  const allowedValues: LexiconAllowedValues = useMemo(
    () =>
      counts ? { categories: counts.categories, entryKinds: counts.entry_kinds } : NO_ALLOWED_VALUES,
    [counts],
  );
  const optionsLoaded = allowedValues.categories.length > 0 && allowedValues.entryKinds.length > 0;

  // Guards against a stale-response race: a slow fetch for an earlier
  // search/category/activeOnly combination can resolve after a newer one
  // has already started. Each loadRows call claims the next id and only
  // applies its result if it is still the most recent call in flight.
  const rowsRequestIdRef = useRef(0);

  // Row list: refetched on debounced search/category/activeOnly changes.
  const loadRows = useCallback(async () => {
    const requestId = ++rowsRequestIdRef.current;
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({
        active_only: activeOnly ? "true" : "false",
        limit: "250",
      });
      if (search.trim()) params.set("search", search.trim());
      if (category) params.set("category", category);
      const rowsRes = await authFetchJson<RowsResponse>(`/api/admin/lexicon/rows?${params}`);
      if (rowsRequestIdRef.current !== requestId) return; // a newer request already landed
      setRows(rowsRes.rows ?? []);
      setTotal(rowsRes.total ?? 0);
    } catch (err) {
      if (rowsRequestIdRef.current !== requestId) return;
      setError(err instanceof Error ? err.message : "Laden mislukt.");
    } finally {
      if (rowsRequestIdRef.current === requestId) setLoading(false);
    }
  }, [search, category, activeOnly]);

  // Category counts: aggregate over the whole table, so this is only worth
  // refetching when the row set actually changes (mount, or a mutation) —
  // not on every keystroke in the search box.
  const loadCounts = useCallback(async () => {
    try {
      const countsRes = await authFetchJson<CountsResponse>(
        `/api/admin/lexicon/category-counts?active_only=${activeOnly}`,
      );
      setCounts(countsRes);
      setError("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Laden mislukt.");
    }
  }, [activeOnly]);

  useEffect(() => {
    const timer = setTimeout(() => void loadRows(), 250);
    return () => clearTimeout(timer);
  }, [loadRows]);

  useEffect(() => {
    void loadCounts();
  }, [loadCounts]);

  const deactivate = useCallback(
    async (row: LexiconRow) => {
      if (!window.confirm(`"${row.term}" deactiveren?`)) return;
      try {
        await authFetchJson(`/api/admin/lexicon/rows/${row.id}`, { method: "DELETE" });
        await Promise.all([loadRows(), loadCounts()]);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Deactiveren mislukt.");
      }
    },
    [loadRows, loadCounts],
  );

  const noteChange = useCallback((epoch: number) => {
    setChangeNotice(propagationLabel(effectivePropagation(epoch, null)));
  }, []);

  const openCreateForm = useCallback(() => {
    setCreateForm(emptyLexiconFormState());
    setCreateErrors([]);
    setShowCreateForm(true);
  }, []);

  const closeCreateForm = useCallback(() => {
    setShowCreateForm(false);
    setCreateErrors([]);
  }, []);

  const submitCreate = useCallback(async () => {
    const built = buildCreatePayload(createForm, allowedValues);
    if (built.payload === null) {
      setCreateErrors(built.errors);
      return;
    }
    setCreating(true);
    setCreateErrors([]);
    try {
      const result = await authFetchJson<RowWriteResponse>("/api/admin/lexicon/rows", {
        method: "POST",
        body: JSON.stringify(built.payload),
      });
      setShowCreateForm(false);
      noteChange(result.epoch);
      await Promise.all([loadRows(), loadCounts()]);
    } catch (err) {
      setCreateErrors([
        err instanceof ApiError ? err.message : "Aanmaken mislukt.",
      ]);
    } finally {
      setCreating(false);
    }
  }, [createForm, allowedValues, loadRows, loadCounts, noteChange]);

  const openEdit = useCallback((row: LexiconRow) => {
    if (!isLexiconRowEditable(row)) return; // guarded row: no edit affordance is shown for this
    setEditingId(row.id);
    setEditForm(lexiconFormStateFromRow(row));
    setEditErrors([]);
  }, []);

  const closeEdit = useCallback(() => {
    setEditingId(null);
    setEditErrors([]);
  }, []);

  const submitEdit = useCallback(
    async (row: LexiconRow) => {
      const built = buildPatchPayload(row, editForm, allowedValues);
      if (built.payload === null) {
        setEditErrors(built.errors);
        return;
      }
      setSaving(true);
      setEditErrors([]);
      try {
        const result = await authFetchJson<RowWriteResponse>(`/api/admin/lexicon/rows/${row.id}`, {
          method: "PATCH",
          body: JSON.stringify(built.payload),
        });
        setEditingId(null);
        noteChange(result.epoch);
        await Promise.all([loadRows(), loadCounts()]);
      } catch (err) {
        setEditErrors([err instanceof ApiError ? err.message : "Opslaan mislukt."]);
      } finally {
        setSaving(false);
      }
    },
    [editForm, allowedValues, loadRows, loadCounts, noteChange],
  );

  return (
    <div className="space-y-6">
      <nav className="flex gap-2">
        {(["termen", "import", "audit"] as SubTab[]).map((name) => (
          <button
            key={name}
            type="button"
            onClick={() => setSubTab(name)}
            className={
              subTab === name
                ? "rounded-lg bg-[var(--cs-accent)] px-3 py-1.5 text-sm text-white"
                : "rounded-lg px-3 py-1.5 text-sm text-[var(--cs-ink-muted)]"
            }
          >
            {name === "termen" ? "Termen" : name === "import" ? "Import" : "Audit"}
          </button>
        ))}
      </nav>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {subTab === "termen" ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Zoek een term…"
              className="rounded-lg border border-[var(--cs-line)] px-3 py-2 text-sm"
            />
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={activeOnly}
                onChange={(event) => setActiveOnly(event.target.checked)}
              />
              Alleen actieve
            </label>
            <span className="text-sm text-[var(--cs-ink-muted)]">
              {rows.length.toLocaleString("nl-BE")} van {total.toLocaleString("nl-BE")} termen
              getoond
              {counts ? ` · ${counts.uncategorized.toLocaleString("nl-BE")} zonder categorie` : ""}
            </span>
            <button
              type="button"
              onClick={() => (showCreateForm ? closeCreateForm() : openCreateForm())}
              className="ml-auto rounded-lg border border-[var(--cs-line)] px-3 py-1.5 text-sm"
            >
              {showCreateForm ? "Annuleer" : "+ Term"}
            </button>
          </div>

          {changeNotice ? (
            <p className="text-sm text-[var(--cs-ink-muted)]">{changeNotice}</p>
          ) : null}

          {showCreateForm ? (
            <LexiconRowFields
              legend="Nieuwe term"
              form={createForm}
              onChange={setCreateForm}
              errors={createErrors}
              busy={creating}
              includeEntryKind
              categories={allowedValues.categories}
              entryKinds={allowedValues.entryKinds}
              optionsLoaded={optionsLoaded}
              onCancel={closeCreateForm}
              onSubmit={() => void submitCreate()}
              submitLabel="Term toevoegen"
            />
          ) : null}

          {rows.length < total ? (
            <p className="text-sm text-amber-700">
              Niet alle {total.toLocaleString("nl-BE")} gevonden termen worden getoond. Verfijn de
              zoekopdracht of het categoriefilter om de rest te zien.
            </p>
          ) : null}

          {counts ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setCategory("")}
                className="rounded-full border border-[var(--cs-line)] px-3 py-1 text-xs"
              >
                alle
              </button>
              {Object.entries(counts.by_category).map(([name, count]) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => setCategory(name)}
                  className={
                    category === name
                      ? "rounded-full bg-[var(--cs-accent)] px-3 py-1 text-xs text-white"
                      : "rounded-full border border-[var(--cs-line)] px-3 py-1 text-xs"
                  }
                >
                  {name} {count.toLocaleString("nl-BE")}
                </button>
              ))}
            </div>
          ) : null}

          {loading ? (
            <p className="text-sm text-[var(--cs-ink-muted)]">Laden…</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-[var(--cs-ink-muted)]">
                  <th className="py-2">Term</th>
                  <th>Discipline</th>
                  <th>Gewicht</th>
                  <th>Categorie</th>
                  <th>Bron</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) =>
                  editingId === row.id ? (
                    <tr key={row.id} className="border-t border-[var(--cs-line)]">
                      <td colSpan={6} className="py-3">
                        <LexiconRowFields
                          legend={`"${row.term}" bewerken`}
                          form={editForm}
                          onChange={setEditForm}
                          errors={editErrors}
                          busy={saving}
                          includeEntryKind={false}
                          categories={allowedValues.categories}
                          entryKinds={allowedValues.entryKinds}
                          optionsLoaded={optionsLoaded}
                          onCancel={closeEdit}
                          onSubmit={() => void submitEdit(row)}
                          submitLabel="Opslaan"
                        />
                      </td>
                    </tr>
                  ) : (
                    <tr key={row.id} className="border-t border-[var(--cs-line)]">
                      <td className="py-2">
                        {row.term}
                        {row.managed ? (
                          <span
                            title="Deze rij wordt opnieuw opgebouwd door import_medical_lexicon.py en kan hier niet gewijzigd worden."
                            className="ml-2 text-xs text-[var(--cs-ink-muted)]"
                          >
                            🔒
                          </span>
                        ) : null}
                      </td>
                      <td>{row.discipline}</td>
                      <td>{row.occurrence_weight}</td>
                      <td>{row.category ?? "—"}</td>
                      <td className="text-xs text-[var(--cs-ink-muted)]">{row.source}</td>
                      <td className="text-right">
                        {isLexiconRowEditable(row) ? (
                          <div className="flex justify-end gap-3">
                            <button
                              type="button"
                              onClick={() => openEdit(row)}
                              className="text-xs text-[var(--cs-accent)]"
                            >
                              Bewerk
                            </button>
                            <button
                              type="button"
                              onClick={() => void deactivate(row)}
                              className="text-xs text-red-600"
                            >
                              Deactiveer
                            </button>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          )}
        </div>
      ) : null}

      {subTab === "import" ? <AdminLexiconImport /> : null}

      {subTab === "audit" ? <AdminLexiconAudit /> : null}
    </div>
  );
}

const FIELD_LABEL = "mb-1 block text-xs text-[var(--cs-ink-muted)]";
const FIELD_INPUT = "w-full rounded-lg border border-[var(--cs-line)] px-2 py-1.5 text-sm";

/**
 * The field set shared by the "+ Term" form and an inline row edit. Kept as
 * one component so the two never drift into different labels or layouts —
 * `includeEntryKind` is the only structural difference, because `entry_kind`
 * is accepted on create but is not one of the PATCH-editable fields.
 */
/**
 * The current value, folded into the loaded options list if it is not
 * already there — so a select never silently drops what the row (or a
 * default form value) already holds during the brief window before
 * `/category-counts` has answered.
 */
function withCurrentValue(options: readonly string[], current: string): readonly string[] {
  if (!current || options.includes(current)) return options;
  return [...options, current];
}

function LexiconRowFields({
  legend,
  form,
  onChange,
  errors,
  busy,
  includeEntryKind,
  categories,
  entryKinds,
  optionsLoaded,
  onCancel,
  onSubmit,
  submitLabel,
}: {
  legend: string;
  form: LexiconFormState;
  onChange: (next: LexiconFormState) => void;
  errors: string[];
  busy: boolean;
  includeEntryKind: boolean;
  categories: readonly string[];
  entryKinds: readonly string[];
  optionsLoaded: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  submitLabel: string;
}) {
  const update = (patch: Partial<LexiconFormState>) => onChange({ ...form, ...patch });
  const categoryOptions = withCurrentValue(categories, form.category);
  const entryKindOptions = withCurrentValue(entryKinds, form.entryKind);

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
      className="space-y-3 rounded-lg border border-[var(--cs-line)] p-4"
    >
      <p className="text-sm font-medium">{legend}</p>

      {errors.length > 0 ? (
        <ul className="list-disc space-y-0.5 pl-5 text-xs text-red-700">
          {errors.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      ) : null}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <div>
          <label className={FIELD_LABEL}>Term *</label>
          <input
            value={form.term}
            onChange={(event) => update({ term: event.target.value })}
            className={FIELD_INPUT}
          />
        </div>
        <div>
          <label className={FIELD_LABEL}>Discipline</label>
          <input
            value={form.discipline}
            onChange={(event) => update({ discipline: event.target.value })}
            className={FIELD_INPUT}
          />
        </div>
        <div>
          <label className={FIELD_LABEL}>Subgroep</label>
          <input
            value={form.subgroup}
            onChange={(event) => update({ subgroup: event.target.value })}
            className={FIELD_INPUT}
          />
        </div>
        {includeEntryKind ? (
          <div>
            <label className={FIELD_LABEL}>Soort</label>
            <select
              value={form.entryKind}
              onChange={(event) => update({ entryKind: event.target.value })}
              disabled={!optionsLoaded}
              className={FIELD_INPUT}
            >
              {entryKindOptions.length === 0 ? <option value="">Laden…</option> : null}
              {entryKindOptions.map((kind) => (
                <option key={kind} value={kind}>
                  {kind}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        <div>
          <label className={FIELD_LABEL}>Gewicht</label>
          <select
            value={form.occurrenceWeight}
            onChange={(event) => update({ occurrenceWeight: event.target.value })}
            className={FIELD_INPUT}
          >
            {OCCURRENCE_WEIGHTS.map((weight) => (
              <option key={weight} value={weight}>
                {weight}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={FIELD_LABEL}>Categorie</label>
          <select
            value={form.category}
            onChange={(event) => update({ category: event.target.value })}
            disabled={!optionsLoaded}
            className={FIELD_INPUT}
          >
            <option value="">— geen —</option>
            {categoryOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={FIELD_LABEL}>Canonieke term</label>
          <input
            value={form.canonicalTerm}
            onChange={(event) => update({ canonicalTerm: event.target.value })}
            className={FIELD_INPUT}
          />
        </div>
        <div>
          <label className={FIELD_LABEL}>Fonetische variant 1</label>
          <input
            value={form.phoneticAlternative1}
            onChange={(event) => update({ phoneticAlternative1: event.target.value })}
            className={FIELD_INPUT}
          />
        </div>
        <div>
          <label className={FIELD_LABEL}>Fonetische variant 2</label>
          <input
            value={form.phoneticAlternative2}
            onChange={(event) => update({ phoneticAlternative2: event.target.value })}
            className={FIELD_INPUT}
          />
        </div>
        <div className="col-span-2 md:col-span-3">
          <label className={FIELD_LABEL}>Gebruikscontexten (kommagescheiden)</label>
          <input
            value={form.usageContexts}
            onChange={(event) => update({ usageContexts: event.target.value })}
            placeholder="bv. ecg, anamnese, ontslagbrief"
            className={FIELD_INPUT}
          />
        </div>
      </div>

      {!optionsLoaded ? (
        <p className="text-xs text-[var(--cs-ink-muted)]">
          Toegestane categorieën en soorten worden nog geladen…
        </p>
      ) : null}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={busy || !optionsLoaded}
          className="rounded-lg bg-[var(--cs-accent)] px-3 py-1.5 text-sm text-white disabled:opacity-40"
        >
          {submitLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="rounded-lg border border-[var(--cs-line)] px-3 py-1.5 text-sm disabled:opacity-40"
        >
          Annuleer
        </button>
      </div>
    </form>
  );
}
